import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FaBars, FaTimes } from 'react-icons/fa';

const NAV_LINKS = [
  { label: 'For Job Seekers', href: '#seekers', landingOnly: true },
  { label: 'For Recruiters',  href: 'https://recruiter.jobxbharat.com/', landingOnly: true },
  { label: 'For Colleges',    href: '#colleges', landingOnly: true },
  // { label: 'Govt Jobs',       href: '/login' }, // temporarily hidden
  { label: 'Walk-in Drives',  href: '/walkin-drives' },
  { label: 'Resume Builder',  href: '/resume-builder' },
  // { label: 'Community',       href: 'https://community.jobxbharat.com/' }, // temporarily hidden
  { label: 'Blogs',           href: '/blogs' },
];

export default function Navbar({ onSignIn, onPostJob, user, onLogout, isAuthenticated }) {
  const navigate = useNavigate();
  const location = useLocation();

  // The "For Job Seekers / Recruiters / Colleges" links point at landing-page anchors,
  // so only show them on the landing page ("/"). Hide them elsewhere (e.g. /search).
  const isLanding = location.pathname === '/';
  const navLinks = NAV_LINKS.filter(l => isLanding || !l.landingOnly);
  const [scrolled,     setScrolled]     = useState(false);
  const [menuOpen,     setMenuOpen]     = useState(false);
  const [profileOpen,  setProfileOpen]  = useState(false);
  const profileRef = React.useRef(null);

  // Close profile dropdown on outside click
  React.useEffect(() => {
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close menu on resize to desktop
  useEffect(() => {
    const onResize = () => { if (window.innerWidth >= 1024) setMenuOpen(false); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const closeMenu = () => setMenuOpen(false);

  // Token in localStorage is set before navigate fires, so it's the reliable auth signal.
  const loggedIn = isAuthenticated || !!user || !!localStorage.getItem('token');

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300
          ${scrolled || menuOpen ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-white/60 backdrop-blur-sm'}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-[64px] md:h-[72px] flex items-center justify-between gap-4">

          {/* Brand */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <img src={require('../../assets/logo.png')} alt="JobxBharat" className="w-7 h-7 md:w-8 md:h-8 object-contain" />
            <span className="font-brand font-black text-lg md:text-xl tracking-tight text-ink-900">
              Jobx<span className="text-brand-saffron">Bharat</span>
            </span>
          </Link>

          {/* Nav links — desktop only */}
          <nav className="hidden lg:flex items-center gap-6">
            {navLinks.map(({ label, href, comingSoon }) =>
              comingSoon ? (
                <span
                  key={label}
                  className="flex items-center gap-1.5 text-sm font-semibold text-ink-300 cursor-default whitespace-nowrap"
                >
                  {label}
                  <span className="px-1.5 py-0.5 rounded-full bg-amber-50 border border-amber-200
                                   text-amber-500 text-[9px] font-black tracking-wide uppercase">
                    Soon
                  </span>
                </span>
              ) : href.startsWith('/') ? (
                // Internal route: use client-side nav so app/auth state persists (no full reload)
                <Link
                  key={href}
                  to={href}
                  className="text-sm font-semibold text-ink-500 hover:text-ink-900 transition-colors whitespace-nowrap"
                >
                  {label}
                </Link>
              ) : (
                <a
                  key={href}
                  href={href}
                  className="text-sm font-semibold text-ink-500 hover:text-ink-900 transition-colors whitespace-nowrap"
                >
                  {label}
                </a>
              )
            )}
          </nav>

          {/* CTAs — desktop */}
          <div className="hidden lg:flex items-center gap-3 shrink-0">
            {loggedIn ? (
              <div className="relative" ref={profileRef}>
                <button
                  onClick={() => setProfileOpen(o => !o)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-xl border border-gray-200
                             bg-white hover:border-brand-purple/40 transition-all"
                >
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-brand-purple to-brand-saffron
                                  flex items-center justify-center text-white text-xs font-black">
                    {(user?.full_name || user?.username || 'Me')[0].toUpperCase()}
                  </div>
                  <span className="text-sm font-semibold text-ink-900 max-w-[100px] truncate">
                    {user?.full_name || user?.username || 'My Account'}
                  </span>
                  <svg className="w-3 h-3 text-ink-300" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path d="M6 9l6 6 6-6"/>
                  </svg>
                </button>
                {profileOpen && (
                  <div className="absolute right-0 top-full mt-2 w-44 bg-white border border-gray-100
                                  rounded-xl shadow-glass-lg overflow-hidden z-50">
                    <div className="px-4 py-3 border-b border-gray-50">
                      <p className="text-xs font-semibold text-ink-900 truncate">{user?.full_name || user?.username || 'My Account'}</p>
                      {user?.email && <p className="text-xs text-ink-300 truncate mt-0.5">{user.email}</p>}
                    </div>
                    <button
                      onClick={() => { navigate('/profile'); setProfileOpen(false); }}
                      className="w-full text-left px-4 py-3 text-sm font-semibold text-ink-900
                                 hover:bg-gray-50 transition-colors flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z"/>
                      </svg>
                      My Profile
                    </button>
                    <button
                      onClick={() => { onLogout(); setProfileOpen(false); }}
                      className="w-full text-left px-4 py-3 text-sm font-semibold text-red-500
                                 hover:bg-red-50 transition-colors flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/>
                      </svg>
                      Log Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <button
                  onClick={onSignIn}
                  className="text-sm font-semibold text-ink-700 hover:text-ink-900 transition-colors"
                >
                  Log In
                </button>
                <a
                  href="https://recruiter.jobxbharat.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 rounded-xl text-sm font-black tracking-wide text-white
                             bg-brand-saffron hover:brightness-110 active:scale-95 transition-all shadow-saffron"
                >
                  Post a Job
                </a>
                <button
                  onClick={onSignIn}
                  className="px-4 py-2 rounded-xl text-sm font-black tracking-wide text-white
                             bg-brand-purple hover:brightness-110 active:scale-95 transition-all shadow-purple"
                >
                  Find Jobs
                </button>
              </>
            )}
          </div>

          {/* Mobile right — avatar/Find Jobs + Hamburger */}
          <div className="flex lg:hidden items-center gap-2">
            {loggedIn ? (
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-purple to-brand-saffron
                              flex items-center justify-center text-white text-xs font-black">
                {(user?.full_name || user?.username || 'Me')[0].toUpperCase()}
              </div>
            ) : (
              <button
                onClick={onSignIn}
                className="px-3 py-1.5 rounded-xl text-xs font-black tracking-wide text-white
                           bg-brand-purple hover:brightness-110 active:scale-95 transition-all focus:outline-none"
              >
                Find Jobs
              </button>
            )}
            <button
              onClick={() => setMenuOpen(o => !o)}
              className="w-9 h-9 flex items-center justify-center rounded-xl
                         text-ink-700 hover:bg-gray-100 transition-colors focus:outline-none"
              aria-label="Toggle menu"
            >
              {menuOpen ? <FaTimes className="text-lg" /> : <FaBars className="text-lg" />}
            </button>
          </div>

        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="lg:hidden border-t border-gray-100 bg-white/95 backdrop-blur-md px-4 pb-5 pt-3">
            <nav className="flex flex-col gap-1 mb-4">
              {navLinks.map(({ label, href, comingSoon }) =>
                comingSoon ? (
                  <span
                    key={label}
                    className="flex items-center justify-between px-3 py-3 rounded-xl
                               text-sm font-semibold text-ink-300"
                  >
                    {label}
                    <span className="px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200
                                     text-amber-500 text-[9px] font-black tracking-wide uppercase">
                      Soon
                    </span>
                  </span>
                ) : href.startsWith('/') ? (
                  <Link
                    key={href}
                    to={href}
                    onClick={closeMenu}
                    className="px-3 py-3 rounded-xl text-sm font-semibold text-ink-700
                               hover:bg-gray-50 hover:text-ink-900 transition-colors"
                  >
                    {label}
                  </Link>
                ) : (
                  <a
                    key={href}
                    href={href}
                    onClick={closeMenu}
                    className="px-3 py-3 rounded-xl text-sm font-semibold text-ink-700
                               hover:bg-gray-50 hover:text-ink-900 transition-colors"
                  >
                    {label}
                  </a>
                )
              )}
            </nav>
            <div className="flex flex-col gap-2 pt-3 border-t border-gray-100">
              {loggedIn ? (
                <>
                  <div className="px-3 py-2">
                    <p className="text-sm font-semibold text-ink-900">{user?.full_name || user?.username || 'My Account'}</p>
                    {user?.email && <p className="text-xs text-ink-300 mt-0.5">{user.email}</p>}
                  </div>
                  <button
                    onClick={() => { navigate('/profile'); closeMenu(); }}
                    className="w-full py-3 rounded-xl text-sm font-black text-ink-700
                               border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    My Profile
                  </button>
                  <button
                    onClick={() => { onLogout(); closeMenu(); }}
                    className="w-full py-3 rounded-xl text-sm font-black text-red-500
                               border border-red-100 hover:bg-red-50 transition-colors"
                  >
                    Log Out
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => { onSignIn(); closeMenu(); }}
                    className="w-full py-3 rounded-xl text-sm font-black text-ink-700
                               border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    Log In
                  </button>
                  <a
                    href="https://recruiter.jobxbharat.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={closeMenu}
                    className="w-full py-3 rounded-xl text-sm font-black text-center text-white
                               bg-brand-saffron hover:brightness-110 transition-all"
                  >
                    Post a Job Free
                  </a>
                </>
              )}
            </div>
          </div>
        )}
      </header>
    </>
  );
}
